import { OrganizationId, ResponseId, SurveyId, Currency } from "@/utils/types";

export const NO_COLLECT_EMAIL_PARAM = "__ncemail";
export const LISTEN_SRC_PARAM = "src";
// TODO(soon): delete this once we match with all possible external recruits
export const LISTEN_SRC_PARAM_VALUES = ["s", "kv"] as const;

export const REDACTED_EMAIL = "[redacted email]";

// ============================================================================
// ENUMS & UNION TYPES
// ============================================================================

export type IncentiveStatus =
  | "no_reward_promised"
  | "response_incomplete"
  | "no_email"
  | "email_collection_disabled"
  | "email_redacted"
  | "payment_not_sent"
  | "payment_sent"
  | "not_self_recruited";

// ============================================================================
// GENERAL INCENTIVE TYPES
// ============================================================================

/**
 * Unified type containing all information needed for processing a response incentive
 */
export type IncentiveInfoForResponseBase = {
  responseId: ResponseId;
  email: string;
  amount: number;
  currency: Currency;
};

export type IncentiveInfoForResponse = IncentiveInfoForResponseBase & {
  orderId: string;
};

export type PayIncentivesOutput = {
  success: boolean;
  message: string;
  processed_count: number;
  successful_responses: ResponseId[];
  failed_responses: ResponseId[];
  previously_paid_response_ids?: ResponseId[];
};

export type IncentiveStatusDetailed =
  | {
      canPayIncentive: false;
      status:
        | "no_reward_promised"
        | "no_email"
        | "email_collection_disabled"
        | "email_redacted"
        | "response_incomplete"
        | "not_self_recruited"; // We might potentially have incentive amount and currency response_incomplete / not_self_recruited / no_email, but they shouldn't be paid.
      displayReason: string;
    }
  | {
      canPayIncentive: true;
      status: "payment_not_sent";
      displayReason: string;
      incentiveAmount: number;
      incentiveEmail: string;
      incentiveCurrency: Currency;
    }
  | {
      canPayIncentive: false;
      status: "payment_sent";
      displayReason: string;
      incentiveAmount: number;
      incentiveEmail: string;
      incentiveCurrency: Currency;
    };

// Note: IncentiveOrder and LedgerEntry types are now defined in @/utils/types
// They are imported from the database schema types for consistency

// ============================================================================
// LEDGER TYPES
// ============================================================================

export type OrganizationLedgerInformation = {
  totalCredits: number;
  totalDebits: number;
  borrowingLimit: number;
  currency: Currency;
};

export type LedgerOrderInfo = {
  org_id: OrganizationId;
  survey_id: SurveyId;
  amount: number;
  currency: Currency;
  order_id: string;
};

export type LedgerChargeInfo = {
  org_id: OrganizationId;
  order_id: string;
  survey_id: SurveyId;
  amount: number;
  currency: Currency;
};

// ============================================================================
// TREMENDOUS TYPES
// ============================================================================

/**
 * Result of attempting to create a Tremendous order
 */
export type TremendousOrderResult = {
  responseId: ResponseId;
  orderId: string;
  amount: number;
  currency: Currency;
} & (
  | { success: true; tremendousOrder: TremendousOrderResponse }
  | { success: false; error: string }
);

/**
 * Batch result for processing multiple Tremendous orders
 */
export type BatchTremendousResult = {
  successful: Array<TremendousOrderResult & { success: true }>;
  failed: Array<TremendousOrderResult & { success: false }>;
};

export type TremendousOrderRequest = {
  payment: {
    funding_source_id: string;
  };
  external_id: string;
  reward: {
    value: {
      denomination: number;
      currency_code: string;
    };
    delivery: {
      method: string;
    };
    recipient: {
      email: string;
      name: string;
    };
    campaign_id: string;
    custom_fields: Array<{
      id: string;
      value: string;
    }>;
    language: string;
  };
};

export type TremendousOrderResponse = {
  order: {
    id: string;
    external_id: string | null;
    campaign_id: string;
    created_at: string;
    channel: string;
    status: string;
    payment: {
      subtotal: number;
      total: number;
      fees: number;
      discount: number;
    };
    rewards: Array<{
      id: string;
      order_id: string;
      created_at: string;
      value: {
        denomination: number;
        currency_code: string;
      };
      recipient: {
        email: string;
        name: string;
        phone: string;
      };
      delivery: {
        method: string;
        status: string;
      };
    }>;
  };
};

export type TremendousApiError = {
  errors: Array<{
    message: string;
    code?: string;
    field?: string;
  }>;
};
